import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const AppleAuthCallback = ({ setUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const processAppleCallback = async () => {
      try {
        const success = searchParams.get('success');
        const exchangeCode = searchParams.get('code');
        const errorParam = searchParams.get('error');

        if (errorParam) {
          throw new Error(`Apple Sign In error: ${errorParam}`);
        }

        // New flow: exchange the code for token
        if (exchangeCode) {
          try {
            const response = await axios.post(`${API}/auth/apple/exchange`, {
              code: exchangeCode
            });
            
            if (response.data.token && response.data.user) {
              localStorage.setItem('token', response.data.token);
              if (setUser) setUser(response.data.user);
              toast.success(`Welcome, ${response.data.user.name}!`);
              
              // Check for pending review
              const pendingReview = sessionStorage.getItem('pendingReview');
              if (pendingReview) {
                try {
                  const reviewData = JSON.parse(pendingReview);
                  await axios.post(`${API}/reviews`, {
                    restaurant_name: reviewData.restaurant_name,
                    address: reviewData.address,
                    phone: reviewData.phone,
                    city: reviewData.city,
                    state: reviewData.state,
                    overall_rating: reviewData.overall_rating,
                    general_condition: parseInt(reviewData.general_condition),
                    supplies: parseInt(reviewData.supplies),
                    maintenance: parseInt(reviewData.maintenance),
                    restroom_types: reviewData.restroom_types,
                    comments: reviewData.comments,
                    fun_url: reviewData.fun_url || ''
                  }, {
                    headers: { Authorization: `Bearer ${response.data.token}` }
                  });
                  sessionStorage.removeItem('pendingReview');
                  toast.success('Your review has been submitted!');
                } catch (e) {
                  console.error('Failed to submit pending review:', e);
                }
              }
              
              navigate('/');
              return;
            }
          } catch (e) {
            console.error('Failed to exchange code:', e);
            throw new Error('Failed to complete sign in');
          }
        }

        // Legacy flow: token already in localStorage
        if (success === 'true') {
          const token = localStorage.getItem('token');
          const userDataStr = localStorage.getItem('apple_auth_user');
          
          if (token && userDataStr) {
            try {
              const userData = JSON.parse(decodeURIComponent(userDataStr));
              if (setUser) setUser(userData);
              toast.success(`Welcome, ${userData.name}!`);
              localStorage.removeItem('apple_auth_user');
              navigate('/');
              return;
            } catch (e) {
              console.error('Failed to parse user data:', e);
            }
          }
          
          if (token) {
            try {
              const response = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (setUser) setUser(response.data);
              toast.success(`Welcome, ${response.data.name}!`);
              navigate('/');
              return;
            } catch (e) {
              console.error('Failed to get user info:', e);
            }
          }
        }

        // If we reach here, something went wrong
        throw new Error('Authentication failed. Please try again.');
        
      } catch (err) {
        console.error('Apple auth error:', err);
        setError(err.message || 'Apple Sign In failed');
        toast.error(err.message || 'Apple Sign In failed');
        setTimeout(() => navigate('/auth'), 3000);
      } finally {
        setProcessing(false);
      }
    };

    processAppleCallback();
  }, [navigate, setUser, searchParams]);

  if (error) {
    return (
      <div className="min-h-screen toilet-humor-bg flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg border-4 border-black max-w-md">
          <div className="text-4xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-red-600 mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Sign In Failed
          </h2>
          <p className="text-gray-700 mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {error}
          </p>
          <p className="text-gray-500 text-sm" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Redirecting to login page...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen toilet-humor-bg flex items-center justify-center">
      <div className="text-center">
        <div className="text-2xl font-bold text-red-600 mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          Processing Apple Sign In...
        </div>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
      </div>
    </div>
  );
};

export default AppleAuthCallback;
